import { useEffect, useId, useState } from 'react';
import ReactModal from 'react-modal';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { useCanRenouncePreferences } from 'hooks/useCanRenouncePreferences';
import {
  NotificationRow,
  destinoDoAviso,
  familyAccent,
  rowText,
  rowTitle,
  toReadPayload,
} from 'pages/Notificacoes/notification-inbox';
import { getNotificationDetail, getNotificationInbox, markAsRead } from 'services/notificationInbox.service';
import { useAppInerte } from 'utils/app-inerte';
import { FOCO_VISIVEL } from 'utils/foco';

/** Os avisos mais recentes; a caixa guarda 90 dias e raramente passa de algumas dezenas. */
const LIMITE = 30;

/** Raiz da aplicação, escondida do leitor de tela enquanto o painel está aberto (como no `ModalFrame`). */
const APP_ROOT_ID = 'root';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Painel lateral de notificações do `notificacoes.js` do design: abre por cima da página, à
 * direita, e lista cada aviso como um cartão na cor do seu assunto, só com título, frase curta e
 * há quanto tempo chegou. Clicar leva direto ao destino do aviso (`cta_url`) e o marca como lido;
 * nenhuma peça de e-mail é renderizada aqui.
 */
export function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const titleId = useId();
  const subtitleId = useId();
  const navigate = useNavigate();
  const canRenouncePreferences = useCanRenouncePreferences();

  useAppInerte(isOpen);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);

    getNotificationInbox({ limit: LIMITE }).then(lista => {
      if (cancelled) return;
      setRows(lista);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  function abrir(row: NotificationRow) {
    const destino = destinoDoAviso(row.inapp?.cta_url);

    marcarComoLido(row);

    if (!destino) return;

    // A aba nova abre ainda dentro do clique, senão o navegador a trata como pop-up.
    if (destino.tipo === 'externo') {
      window.open(destino.url, '_blank', 'noopener,noreferrer');
      return;
    }

    onClose();
    navigate(destino.url);
  }

  /**
   * Best-effort, como na Central antiga: o PUT de leitura é substituição e precisa do documento
   * completo, que só o detalhe traz. Falhar aqui não impede o colaborador de seguir para o destino.
   */
  async function marcarComoLido(row: NotificationRow) {
    if (row.status === 'read') return;

    setRows(atual => atual.map(item => (item._id === row._id ? { ...item, status: 'read' } : item)));

    const detalhe = await getNotificationDetail(row._id);
    if (detalhe && detalhe.status !== 'read') await markAsRead(toReadPayload(detalhe, Date.now()));
  }

  const novas = rows.filter(row => row.status === 'unread').length;

  return (
    <ReactModal
      isOpen={isOpen}
      appElement={document.getElementById(APP_ROOT_ID) ?? undefined}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      aria={{ labelledby: titleId, describedby: subtitleId }}
      portalClassName="relative z-50"
      overlayClassName="fixed inset-0 z-[100] bg-[rgba(0,45,75,0.45)]"
      // D3: 360px, 92vw, 22px e a sombra lateral do design não têm degrau no Tailwind.
      className="fixed inset-y-0 right-0 box-border flex w-[360px] max-w-[92vw] flex-col gap-3 overflow-y-auto bg-surface-100 px-5 py-[22px] font-text shadow-[-18px_0_50px_rgba(17,24,39,0.18)] outline-none animate-jj-drawer motion-reduce:animate-none"
    >
      <div className="flex items-center gap-2.5">
        <h2 id={titleId} className="m-0 font-display text-[19px] font-extrabold text-brand-blue-dark">
          Notificações
        </h2>
        {novas > 0 && (
          <span className="rounded-jj-pill bg-brand-blue px-2.5 py-[3px] font-display text-[11px] font-bold text-brand-yellow">
            {novas === 1 ? '1 nova' : `${novas} novas`}
          </span>
        )}
        <button
          type="button"
          aria-label="Fechar notificações"
          onClick={onClose}
          className={`ml-auto flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-brand-blue text-[13px] leading-none text-brand-yellow transition-colors hover:bg-[#5168FF] ${FOCO_VISIVEL}`}
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>

      <p id={subtitleId} className="mb-0.5 text-xs text-content-soft">
        Reconhecimentos, validações e prazos das suas dinâmicas.
      </p>

      {loading && (
        <p role="status" className="text-xs text-content">
          Carregando notificações…
        </p>
      )}

      {!loading && rows.length === 0 && (
        <p role="status" className="text-xs text-content">
          Nenhuma notificação por aqui. Quando o programa enviar um aviso para você, ele aparece nesta lista.
        </p>
      )}

      {!loading && rows.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {rows.map(row => {
            const texto = rowText(row);

            return (
              <li key={row._id}>
                <button
                  type="button"
                  onClick={() => abrir(row)}
                  className={`block w-full cursor-pointer rounded-jj-lg border-none px-4 py-3.5 text-left text-content shadow-jj-01 transition hover:-translate-x-[3px] hover:shadow-[0_8px_20px_rgba(0,45,75,0.14)] motion-reduce:transition-none ${familyAccent(row.family)} ${FOCO_VISIVEL}`}
                >
                  <span className="block font-display text-[13px] font-bold text-brand-blue-dark">{rowTitle(row)}</span>
                  {texto && <span className="mt-[3px] block text-xs leading-normal">{texto}</span>}
                  <time dateTime={new Date(row.created).toISOString()} className="mt-1.5 block text-[10.5px]">
                    {formatDistanceToNow(row.created, { addSuffix: true, locale: ptBR })}
                  </time>
                  {row.status === 'unread' && <span className="sr-only">Não lida.</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {canRenouncePreferences && (
        <Link
          to="/minhas-preferencias"
          onClick={onClose}
          className={`mt-auto pt-3.5 text-[11px] font-bold uppercase tracking-[.05em] text-brand-blue no-underline hover:text-brand-blue-dark hover:no-underline ${FOCO_VISIVEL}`}
        >
          Ajustar minhas preferências
        </Link>
      )}
    </ReactModal>
  );
}
